import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { auteursAPI, livresAPI } from '../services/api';
import LivreCard from '../components/LivreCard/LivreCard';

function AuteurDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Déterminer la locale pour date-fns
  const dateLocale = i18n.language === 'en' ? enUS : fr;

  const { data: auteur, isLoading: auteurLoading, error: auteurError } = useQuery(
    ['auteur', id],
    () => auteursAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  // Récupérer les livres de cet auteur
  const { data: livresData, isLoading: livresLoading } = useQuery(
    ['livres-auteur', id],
    () => livresAPI.getAll({ auteur_id: id, limit: 100 }).then(res => res.data),
    {
      enabled: !!id,
    }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Mutation pour supprimer l'auteur
  const deleteAuteurMutation = useMutation(
    () => auteursAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('auteurs');
        toast.success(t('auteurDetailPage.deleteSuccess'));
        navigate('/auteurs');
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('auteurDetailPage.deleteErrorWithBooks'));
        } else {
          toast.error(error.response?.data?.message || t('auteurDetailPage.deleteError'));
        }
      }
    }
  );

  const handleEditClick = () => {
    navigate(`/auteurs/${id}/modifier`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteAuteurMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Fonctions pour LivreCard
  const handleView = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  const handleEdit = (livreId) => {
    navigate(`/livres/${livreId}/modifier`);
  };

  const handleAuthorClick = (authorId) => {
    if (authorId) {
      navigate(`/auteurs/${authorId}`);
    }
  };

  const handleEditeurClick = (editeurId) => {
    if (editeurId) {
      navigate(`/editeurs/${editeurId}`);
    }
  };

  const handlePhotoClick = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  const formatNomComplet = (nom, prenom) => {
    return `${prenom || ''} ${nom}`.trim();
  };

  if (auteurLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (auteurError) {
    return (
      <Alert severity="error">
        {t('auteurDetailPage.loadingError', { message: auteurError.message })}
      </Alert>
    );
  }

  if (!auteur) {
    return (
      <Alert severity="info">
        {t('auteurDetailPage.notFound')}
      </Alert>
    );
  }

  const livres = livresData?.data || [];

  // Calculer les statistiques des livres
  const stats = {
    total: livres.length,
    lus: livres.filter(l => l.statut_lecture === 'lu').length,
    enCours: livres.filter(l => l.statut_lecture === 'en_cours').length,
    nonLus: livres.filter(l => l.statut_lecture === 'non_lu').length,
  };

  return (
    <Box>
      {/* Header avec navigation */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton onClick={() => navigate('/auteurs')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
            >
              {t('auteurDetailPage.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                sx={{ 
                  height: '52px',
                  borderRadius: 2,
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {t('auteurDetailPage.edit')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                sx={{ 
                  height: '52px',
                  borderRadius: 2,
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {t('auteurDetailPage.delete')}
              </Button>
            </Box>
        </Box>
      </Box>

      {/* Nom de l'auteur */}
      <Typography 
        variant="h4" 
        component="h2"
        sx={{ 
          mb: 2,
          fontWeight: 700,
          fontSize: '24px',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        }}
      >
        {formatNomComplet(auteur.nom, auteur.prenom)}
      </Typography>

      {/* Nationalité et date de naissance */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {auteur.nationalite && (
          <Typography 
            variant="body1"
            sx={{ 
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {auteur.nationalite}
          </Typography>
        )}
        {auteur.date_naissance && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
            <Typography 
              variant="body1"
              sx={{ 
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {t('auteurDetailPage.bornIn', { year: new Date(auteur.date_naissance).getFullYear() })}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Biographie */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h3"
          sx={{ 
            mb: 1,
            fontWeight: 700,
            fontSize: '16px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
          >
            {t('auteurDetailPage.biography')}
          </Typography>
        {auteur.biographie ? (
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {auteur.biographie}
          </Typography>
        ) : (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontStyle: 'italic',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.noBiography')}
          </Typography>
        )}
      </Box>

      {/* Métadonnées */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
          <Typography 
            variant="h6" 
            component="h3"
            sx={{ 
              fontSize: '12px',
              fontWeight: 600,
              color: 'oklch(0.551 0.027 264.364)',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.metadata')}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: { xs: 0, sm: 2 },
          ml: 0 
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '14px',
              fontWeight: 400,
              color: 'oklch(0.446 0.03 256.802)',
              mb: { xs: 0.5, sm: 0 },
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.createdAt')} {auteur.created_at ? format(new Date(auteur.created_at), 'dd MMMM yyyy', { locale: dateLocale }) : 'Invalid Date'}
          </Typography>
          {auteur.updated_at && auteur.updated_at !== auteur.created_at && (
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                color: 'oklch(0.446 0.03 256.802)',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {t('auteurDetailPage.updatedAt')} {format(new Date(auteur.updated_at), 'dd MMMM yyyy', { locale: dateLocale })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Statistiques */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Carte Total Livres */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'primary.main',
              mb: 0.5,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {stats.total}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {stats.total === 1 ? t('auteurDetailPage.bookCount') : t('auteurDetailPage.bookCountPlural')}
          </Typography>
        </Box>

        {/* Carte Lus */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(46, 125, 50, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'success.main',
              mb: 0.5,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {stats.lus}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.read')}
          </Typography>
        </Box>

        {/* Carte En cours */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(237, 108, 2, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'warning.main',
              mb: 0.5,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {stats.enCours}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'warning.main',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.inProgress')}
          </Typography>
        </Box>

        {/* Carte Non lus */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(97, 97, 97, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'text.secondary',
              mb: 0.5,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {stats.nonLus}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('auteurDetailPage.notRead')}
          </Typography>
        </Box>
      </Box>

      {/* Liste des livres */}
      <Box>
        <Typography 
          variant="h6" 
          component="h3"
          sx={{ 
            mb: 2,
            fontWeight: 700,
            fontSize: '16px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
          >
            {t('auteurDetailPage.booksInLibrary')}
          </Typography>
        
        {livresLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : livres.length === 0 ? (
          <Alert severity="info">
            {t('auteurDetailPage.noBooks')}
          </Alert>
        ) : (
          <>
            {livres.length >= 100 && auteur.nombre_livres > 100 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('auteurDetailPage.showingFirst', { total: auteur.nombre_livres })}
              </Alert>
            )}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(6, 1fr)'
              }, 
              gap: { xs: 2, md: 3 },
              width: '100%'
            }}>
              {livres.map((livre) => (
                <Box key={livre.id} sx={{ width: '100%', minWidth: 0 }}>
                  <LivreCard
                    livre={livre}
                    onView={handleView}
                    onEdit={handleEdit}
                    onAuthorClick={handleAuthorClick}
                    onEditeurClick={handleEditeurClick}
                    onPhotoClick={handlePhotoClick}
                    availableTags={[]}
                    onTagAdd={null}
                    onTagRemove={null}
                    isTagLoading={false}
                    queryClient={queryClient}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('auteurDetailPage.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('auteurDetailPage.deleteConfirmAuthor', { name: formatNomComplet(auteur.nom, auteur.prenom) })}
          </DialogContentText>
          {stats.total > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('auteurDetailPage.deleteWarning', { count: stats.total })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('auteurDetailPage.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteAuteurMutation.isLoading || stats.total > 0}
          >
            {deleteAuteurMutation.isLoading ? t('auteurDetailPage.deleting') : t('auteurDetailPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AuteurDetailPage;

